/**
 * The Dashboards section. Two pages behind one element: the browse list, and one
 * open dashboard.
 *
 * The open dashboard's toolbar owns the shared context - which ships, which date
 * range - through the *same* `<ship-multi-select>` and `<date-range>` components
 * Explore uses, bound to the same atoms. So the section switch keeps your ship
 * and your window, and changing either moves every unpinned tile at once. That
 * is the Grafana model: the dashboard owns the time picker, panels inherit it
 * unless they opt out.
 *
 * Charts are authored in `<chart-builder>`, which mounts the real Explore
 * controls in a dialog rather than reimplementing them.
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  activeSection,
  plottedShipIds,
  selectedShip,
  startDate,
  endDate,
  activePreset,
  comparisonShips,
  catalogAvailable,
  myPermissions,
  isRootUser,
  myUserId,
} from '../../state/store.ts';
import { restoreFromUrl, syncToUrl } from '../../state/url-sync.ts';
import { dashboardIdFromPath } from '../../state/app-route.ts';
import { showToast } from '../../ui/toast.ts';
import { confirmAction } from '../../ui/confirm-dialog.ts';
import {
  activeDashboard,
  activeTab,
  canEditDashboard,
  addLinkedTiles,
  addTab,
  addTile,
  closeDashboard,
  detachTile,
  dashboardDirty,
  dashboardEditing,
  discardActiveChanges,
  dashboardRoute,
  dashboards,
  folders,
  activeDashboardId,
  activeDashboardTabId,
  deleteTab,
  duplicateTile,
  openDashboard,
  saveActiveDashboard,
  chartTiles,
  removeTile,
  setTileIgnoresSlicers,
  renameDashboard,
  renameTab,
  renameTile,
  selectTab,
  type Dashboard as DashboardDocument,
  setTileQuery,
  type DashboardTile,
  LIVE_REFRESH_CHOICES,
  updateDashboardSettings,
  type LiveRefresh,
} from './dashboard-state.ts';
import { dataSources } from '../../state/data-sources.ts';
import { tileExploreQuery, type SharedContext } from './tile-spec.ts';
import { defaultVariables, resolveVariables } from './variables.ts';
import { activeSlicers, clearAllSelections, slicerSelections, trimTimeSelections } from './slicer-state.ts';
import './dashboard-filter-dialog.ts';
import { DashboardConflictError, ensureDashboardsLoaded } from './dashboard-repository.ts';
import { adoptExploreSelection } from './dashboard-context.ts';
import { dashboardLiveSelection, dashboardSelection } from './dashboard-selection.ts';
import { exploreSelection, type SelectionScope } from '../ship/selection-scope.ts';
import { shipLabel } from '../../utils/ships.ts';
import type { BuilderCommit } from './chart-builder.ts';
import type { PickerCommit } from './catalog-picker.ts';
import type { DashDropdown } from './dash-dropdown.ts';
import { composeGrid, exportStem, savePdf, savePng, type ExportProgress } from './dashboard-export.ts';
import { effectiveTile, linkState } from './linked-chart.ts';
import './dash-dropdown.ts';
import './catalog-picker.ts';
import './dashboard-grid.ts';
import './dashboard-browse.ts';
import './dashboard-settings.ts';
import './dashboard-share-dialog.ts';
import './chart-builder.ts';
import '../ship/ship-multi-select.ts';
import '../ship/date-range.ts';
import { PRESET_LABELS, shortDate } from '../../utils/dates.ts';
import { userMessage } from '../../api/errors.ts';

/**
 * Save glyph. An inline stroke SVG on `currentColor`, matching the copy-link
 * icon in `presets-bar.ts` - 12px on a 24 viewBox, 1.8 stroke, round joins - so
 * it inherits the button's text colour in both themes instead of dropping a
 * full-colour emoji into a monochrome toolbar.
 */
const SAVE_GLYPH = svg`<svg
  class="btn-glyph"
  width="12"
  height="12"
  viewBox="0 0 24 24"
  fill="none"
  aria-hidden="true"
>
  <path
    d="M5 4h10.2L20 8.8V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
  <path
    d="M8 4v4.6h6V4M7.6 20v-5.6h8.8V20"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

/** Download arrow into a tray. Same stroke treatment as the save glyph. */
const EXPORT_GLYPH = svg`<svg
  class="btn-glyph"
  width="12"
  height="12"
  viewBox="0 0 24 24"
  fill="none"
  aria-hidden="true"
>
  <path
    d="M12 4v11M7.5 10.5 12 15l4.5-4.5M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

/** Full screen: four corners. */
const FULLSCREEN_GLYPH = svg`<svg
  class="btn-glyph"
  width="12"
  height="12"
  viewBox="0 0 24 24"
  fill="none"
  aria-hidden="true"
>
  <path
    d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

/** Settings gear. Same stroke treatment as the save glyph. */
const GEAR_GLYPH = svg`<svg
  class="btn-glyph"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  aria-hidden="true"
>
  <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8" />
  <path
    d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

/** What the collapsed time picker says it is showing. */
function dateRangeLabel(scope: SelectionScope): string {
  const preset = scope.preset();
  if (preset && preset !== 'custom' && PRESET_LABELS[preset]) return PRESET_LABELS[preset];
  return `${shortDate(scope.start())} - ${shortDate(scope.end())}`;
}

/** "10 s", "1 min" - a refresh choice in words. */
function refreshLabel(choice: LiveRefresh): string {
  if (choice === 'off') return 'Off';
  if (choice === 'cadence') return 'As written';
  return choice < 60 ? `${choice} s` : `${choice / 60} min`;
}

/** What the collapsed Live picker says: its window, and how often it moves. */
function liveLabel(scope: SelectionScope, refresh: LiveRefresh): string {
  const window = dateRangeLabel(scope);
  if (refresh === 'off') return `${window} · paused`;
  return refresh === 'cadence' ? window : `${window} · ${refreshLabel(refresh)}`;
}

/** What the collapsed ship picker says it is showing. */
function shipsLabel(scope: SelectionScope): string {
  const [primary, ...rest] = scope.ships();
  if (!primary) return 'No ship';
  if (rest.length === 0) return shipLabel(primary);
  // Naming the primary matters: it drives columns, dates and Export.
  return `${shipLabel(primary)} +${rest.length}`;
}

@customElement('dashboard-view')
export class DashboardView extends LightElement {
  private store = new StoreController(this, [
    dashboards,
    folders,
    dashboardRoute,
    activeDashboardId,
    activeDashboardTabId,
    dashboardDirty,
    dashboardEditing,
    activeSection,
    selectedShip,
    comparisonShips,
    startDate,
    endDate,
    activePreset,
    catalogAvailable,
    myPermissions,
    isRootUser,
    myUserId,
    slicerSelections,
    dataSources,
  ]);

  @state() private renamingTabId: string | null = null;
  /** The filter dialog, open to add a slicer (`tile: null`) or edit one. */
  @state() private filterDialog: { tile: DashboardTile | null } | null = null;
  @state() private renamingDashboard = false;
  /** Open builder: null = closed, `{ tile: null }` = adding a new chart. */
  @state() private builder: { tile: DashboardTile | null } | null = null;
  @state() private picking = false;
  /** Tiles settled / tiles to draw, across every page of a running export. */
  @state() private exporting: ExportProgress | null = null;
  /**
   * Full screen: the current tab's canvas and nothing else - no app header, no
   * toolbar, no tabs, no tile headers. A way of looking, not a setting, so it
   * belongs to this browser window and is never saved with the dashboard.
   */
  @state() private kiosk = false;
  /** The "Esc to leave" note, shown for a moment on entering full screen. */
  @state() private kioskHint = false;
  private kioskHintTimer: number | null = null;

  /**
   * The dashboard id in a clean `/dashboards/<id>` route, read synchronously.
   *
   * It has to be captured before anything renders: this component's own
   * `updated()` calls `syncToUrl()`, which rewrites the address bar from the
   * current route - and while the store is still loading that route is
   * `browse`, so the rewrite could lose the id before the async bootstrap reads
   * it. The link would open the list every time.
   */
  private wantedDashboardId: string | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.wantedDashboardId =
      dashboardIdFromPath(window.location.pathname) ??
      new URLSearchParams(window.location.search).get('db');
    window.addEventListener('beforeunload', this.onBeforeUnload);
    window.addEventListener('keydown', this.onKeydown);
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    void this.bootstrap();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    this.setKiosk(false);
  }

  /* ── Full screen ──────────────────────────────────────── */

  /**
   * Enter or leave full screen. The browser's own full screen is asked for too,
   * so the page fills the display; when the browser refuses (an iframe, a
   * policy), the dashboard still drops every piece of chrome in the window.
   */
  private setKiosk(on: boolean): void {
    if (on === this.kiosk) return;
    this.kiosk = on;
    document.documentElement.classList.toggle('nde-kiosk', on);
    if (this.kioskHintTimer !== null) window.clearTimeout(this.kioskHintTimer);
    this.kioskHint = on;
    if (on) {
      dashboardEditing.set(false);
      this.renamingTabId = null;
      this.renamingDashboard = false;
      this.kioskHintTimer = window.setTimeout(() => {
        this.kioskHint = false;
      }, 2500);
      void document.documentElement.requestFullscreen?.().catch(() => {
        /* the window-only version is still full screen enough */
      });
    } else if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {});
    }
  }

  /** Esc in the browser's full screen ends it before any keydown arrives. */
  private onFullscreenChange = (): void => {
    if (!document.fullscreenElement && this.kiosk) this.setKiosk(false);
  };

  /** F toggles full screen; in it, Esc leaves and the arrows change tabs. */
  private onKeydown = (e: KeyboardEvent): void => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
    if (activeSection.value !== 'dashboards' || dashboardRoute.value !== 'view' || !activeDashboard()) return;
    const target = e.target;
    if (target instanceof Element
      && target.closest('input, textarea, select, [contenteditable="true"], dialog, [role="dialog"], [role="menu"]')) return;
    if (e.key === 'f' || e.key === 'F') {
      e.preventDefault();
      this.setKiosk(!this.kiosk);
      return;
    }
    if (!this.kiosk) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.setKiosk(false);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      const d = activeDashboard();
      const tabs = d?.tabs ?? [];
      if (tabs.length < 2) return;
      e.preventDefault();
      const i = tabs.findIndex((t) => t.id === activeTab()?.id);
      const step = e.key === 'ArrowRight' ? 1 : -1;
      selectTab(tabs[(i + step + tabs.length) % tabs.length]!.id);
    }
  };

  /** Pick the storage, load the list, then honour the deep link from connect. */
  private async bootstrap(): Promise<void> {
    // Choose the repository here: the server when
    // the account can reach stored dashboards, the browser otherwise. The import
    // of an existing browser store rides along on the first server load.
    await ensureDashboardsLoaded();
    const wanted = this.wantedDashboardId;
    this.wantedDashboardId = null;
    if (wanted && dashboards.value.some((d) => d.id === wanted)) openDashboard(wanted);
  }

  /** Last dashboard-route state written, so unrelated renders do not rewrite the URL. */
  private syncedKey = '';

  /**
   * Whose ship and range the toolbar edits, for the dashboard on screen.
   *
   * Always the dashboard's own, stored in the document: no dashboard follows
   * Explore any more (`dashboard-context.ts`), and Explore's atoms are never
   * touched from here.
   *
   * Kept per dashboard id so the pickers are handed the same object across
   * renders rather than a fresh one every time.
   */
  private scopeFor(dashboard: DashboardDocument | null): SelectionScope {
    if (!dashboard) return exploreSelection;
    // Keyed on the object, not the id: opening a dashboard from the list
    // replaces the row with the fetched document, and a scope still writing
    // into the row would write into a document nothing renders.
    if (this.scopeFrom !== dashboard || !this.dashboardScope) {
      // A dashboard with no ship or window yet starts on Explore's, once.
      adoptExploreSelection(dashboard);
      this.scopeFrom = dashboard;
      this.dashboardScope = dashboardSelection(dashboard);
    }
    return this.dashboardScope;
  }

  private scopeFrom: DashboardDocument | null = null;
  private dashboardScope: SelectionScope | null = null;

  /** The Live picker's scope, kept per document object like `scopeFor`'s. */
  private liveScopeFor(dashboard: DashboardDocument): SelectionScope {
    if (this.liveScopeFrom !== dashboard || !this.liveScope) {
      this.liveScopeFrom = dashboard;
      this.liveScope = dashboardLiveSelection(dashboard);
    }
    return this.liveScope;
  }

  private liveScopeFrom: DashboardDocument | null = null;
  private liveScope: SelectionScope | null = null;

  /** Whether any tile on any tab reads a source without history. */
  private hasLiveTiles(dashboard: DashboardDocument | null): boolean {
    if (!dashboard) return false;
    const live = new Set(dataSources.value.filter((src) => !src.capabilities.history).map((src) => src.id));
    return live.size > 0 && dashboard.tabs.some((tab) => tab.tiles.some((t) => live.has(t.source)));
  }

  /**
   * The toolbar's second time picker, for Live tiles only: Live's short
   * presets, and how often the tiles move on. Shown only on a dashboard that
   * has a Live tile - an EOS board keeps the toolbar it always had.
   */
  private renderLivePicker(dashboard: DashboardDocument | null): TemplateResult | typeof nothing {
    if (!dashboard || !this.hasLiveTiles(dashboard)) return nothing;
    const scope = this.liveScopeFor(dashboard);
    const refresh = dashboard.settings.liveRefresh;
    return html`<dash-dropdown
      caption="Live"
      class="dash-live-picker"
      label=${liveLabel(scope, refresh)}
      panelClass="is-wide"
      alignEnd
      .panel=${() => html`<date-range .scope=${scope}></date-range>
        <div class="dash-live-refresh">
          <span class="dash-live-refresh-label">Refresh Live charts</span>
          <div class="date-presets" role="radiogroup" aria-label="Refresh Live charts">
            ${LIVE_REFRESH_CHOICES.map(
              (choice) => html`<button
                class="date-preset ${refresh === choice ? 'active' : ''}"
                role="radio"
                aria-checked=${refresh === choice ? 'true' : 'false'}
                title=${choice === 'cadence'
                  ? 'As often as each chart\'s data is written'
                  : choice === 'off'
                    ? 'Draw once; do not move on'
                    : `Every ${refreshLabel(choice)}`}
                @click=${() => updateDashboardSettings({ liveRefresh: choice })}
              >
                ${refreshLabel(choice)}
              </button>`,
            )}
          </div>
        </div>`}
    ></dash-dropdown>`;
  }

  /** The dashboard and window the time slicers were last fitted to. */
  private fittedWindow: string | null = null;

  override willUpdate(): void {
    // A time slicer's selection is fixed times; keep it inside the window when
    // the window moves (`trimTimeSelections`). Before render, so the tiles
    // drawn now already see the trimmed selections.
    const dashboard = activeDashboard();
    if (!dashboard) return;
    const { start, end } = this.sharedContext();
    const fitted = `${dashboard.id}|${start}|${end}`;
    if (fitted === this.fittedWindow) return;
    this.fittedWindow = fitted;
    trimTimeSelections(dashboard, start, end);
  }

  override updated(): void {
    if (this.kiosk && (activeSection.value !== 'dashboards' || dashboardRoute.value !== 'view' || !activeDashboard())) {
      this.setKiosk(false);
    }
    if (activeSection.value === 'dashboards') {
      void ensureDashboardsLoaded().catch((error: unknown) => {
        console.warn('Could not refresh dashboards', error);
      });
    }
    // Keep `/dashboards/<id>` in step with what is open.
    //
    // Only when it actually changed, and only while this section is the one on
    // screen. This component stays mounted behind Explore, and syncing from
    // every render is what erased shared plot links: the write landed during
    // `loadShips()`, before `restoreFromUrl()` had read them. `url-sync` now
    // refuses writes that early too, but the narrower trigger is the real fix -
    // there was never a reason to rewrite the URL from here for an Explore
    // re-render.
    if (this.wantedDashboardId !== null) return;
    if (activeSection.value !== 'dashboards') return;
    const key = `${dashboardRoute.value}:${activeDashboardId.value ?? ''}`;
    if (key === this.syncedKey) return;
    this.syncedKey = key;
    syncToUrl();
  }

  /**
   * The values unpinned tiles inherit, read fresh so tiles refetch on change.
   *
   * The dashboard's variables decide where each value comes from; the atoms are
   * only what a `url`-bound variable falls back to, which is every variable
   * today. That is the seam a fixed dashboard's own ship picker writes into -
   * the toolbar stops being the source and becomes one of three.
   */
  private sharedContext(): SharedContext {
    const dashboard = activeDashboard();
    const shared = resolveVariables(dashboard?.variables ?? defaultVariables(), {
      ships: plottedShipIds(),
      start: startDate.value,
      end: endDate.value,
    });
    const liveSources = dataSources.value.filter((src) => !src.capabilities.history).map((src) => src.id);
    const slicers = activeSlicers(dashboard ?? null, activeTab());
    return { ...shared, liveSources, ...(slicers.length ? { slicers } : {}) };
  }

  /* ── Builder ──────────────────────────────────────────── */

  private openBuilder(tile: DashboardTile | null): void {
    if (!activeDashboard()) return;
    this.builder = { tile };
  }

  private onBuilderCommit(e: CustomEvent<BuilderCommit>): void {
    const { tileId, title, pins, query } = e.detail;
    if (tileId) {
      setTileQuery(tileId, query, pins);
      renameTile(tileId, title);
      showToast('success', `Updated "${title}"`);
    } else {
      const tile = addTile(title, pins, query);
      if (tile) showToast('success', `Added "${tile.title}"`);
    }
    this.builder = null;
  }

  /* ── Catalog picker ───────────────────────────────────── */

  private onPickerCommit(e: CustomEvent<PickerCommit>): void {
    const added = addLinkedTiles(e.detail.charts);
    this.picking = false;
    if (added.length === 1) showToast('success', `Added "${added[0]!.title}"`);
    else if (added.length > 1) showToast('success', `Added ${added.length} charts`);
  }

  /** Catalog charts already linked on the open tab, for the picker to mark. */
  private linkedOnTab(): Set<string> {
    const ids = (activeTab()?.tiles ?? []).map((t) => t.chartId);
    return new Set(ids.filter((id): id is string => !!id));
  }

  /* ── Export ───────────────────────────────────────────── */

  /**
   * PNG: the tab on screen as one image. PDF: every tab, a page each. A tab's
   * tiles exist only while it is showing, so the PDF steps through the tabs and
   * puts back the one you were on.
   */
  private async onExport(format: 'png' | 'pdf'): Promise<void> {
    const d = activeDashboard();
    const current = activeTab();
    if (!d || !current || this.exporting) return;
    const tabs = format === 'pdf' ? d.tabs : [current];
    // The exported image says what the dashboard is drawing, which for a
    // detached one is its own ship and range rather than Explore's.
    const scope = this.scopeFor(d);
    const subtitle = `${shipsLabel(scope)} · ${dateRangeLabel(scope)} · exported ${new Date().toLocaleString()}`;
    const totalTiles = tabs.reduce((n, t) => n + chartTiles(t).length, 0);
    // The image follows the screen: titles as shown (Edit always shows them),
    // frames as set.
    const look = {
      titles: d.settings.showTitles || (dashboardEditing.value && canEditDashboard(d)),
      frames: d.settings.tileFrames,
    };
    let before = 0;
    this.exporting = { done: 0, total: totalTiles };
    const pages: HTMLCanvasElement[] = [];
    try {
      for (const tab of tabs) {
        selectTab(tab.id);
        await this.updateComplete;
        const grid = this.querySelector<HTMLElement & { updateComplete: Promise<boolean> }>('dashboard-grid');
        if (!grid) continue;
        await grid.updateComplete;
        const title = d.tabs.length > 1 ? `${d.name} - ${tab.name}` : d.name;
        pages.push(
          await composeGrid(grid, { title, subtitle }, (p) => {
            this.exporting = { done: before + p.done, total: totalTiles };
          }, look),
        );
        before += chartTiles(tab).length;
      }
      const stem = exportStem(d.name, format === 'png' && d.tabs.length > 1 ? current.name : '');
      if (format === 'png') savePng(pages[0]!, stem);
      else await savePdf(pages, stem);
    } catch (e) {
      showToast('error', `Could not export the dashboard: ${userMessage(e)}`);
    } finally {
      selectTab(current.id);
      this.exporting = null;
    }
  }

  /**
   * The ⋯ menu at the end of the toolbar: full screen and export - things done
   * now and then, kept out of the row of controls used all the time. Everyone
   * who can open the dashboard gets it, viewers included. While an export runs
   * the button itself fills as its progress bar.
   */
  private renderMore(): TemplateResult {
    const run = this.exporting;
    if (run) {
      const pct = run.total ? Math.round((run.done / run.total) * 100) : 100;
      return html`<button
        class="dash-btn dash-more-btn dash-export-running"
        disabled
        title=${`Exporting - ${run.done} of ${run.total} charts ready`}
        aria-label="Exporting"
        style=${`--progress:${pct}%`}
      >
        <span>⋯</span>
      </button>`;
    }
    const pick = (e: Event, action: () => void): void => {
      (e.currentTarget as HTMLElement).closest<DashDropdown>('dash-dropdown')?.close();
      action();
    };
    const multi = (activeDashboard()?.tabs.length ?? 1) > 1;
    return html`<dash-dropdown
      class="dash-more-dd"
      triggerClass="dash-more-btn"
      triggerTitle="More"
      panelClass="is-menu"
      panelRole="menu"
      alignEnd
      .triggerContent=${html`⋯`}
      .panel=${() => html`
        <button class="dash-menu-item" role="menuitem" @click=${(e: Event) => pick(e, () => this.setKiosk(true))}>
          <span class="dash-menu-label">${FULLSCREEN_GLYPH} Full screen <kbd class="dash-menu-key">F</kbd></span>
          <span class="dash-menu-hint">Only the charts. Esc leaves${multi ? '; ← → change tabs' : ''}.</span>
        </button>
        <div class="dash-menu-sep" role="separator"></div>
        <button class="dash-menu-item" role="menuitem" @click=${(e: Event) => pick(e, () => void this.onExport('png'))}>
          <span class="dash-menu-label">${EXPORT_GLYPH} Export image (PNG)</span>
          <span class="dash-menu-hint">${multi ? 'This tab, every chart in one picture' : 'Every chart in one picture'}</span>
        </button>
        <button class="dash-menu-item" role="menuitem" @click=${(e: Event) => pick(e, () => void this.onExport('pdf'))}>
          <span class="dash-menu-label">${EXPORT_GLYPH} Export PDF</span>
          <span class="dash-menu-hint">${multi ? 'Every tab, one page each' : 'The dashboard as one page'}</span>
        </button>
      `}
    ></dash-dropdown>`;
  }

  /* ── Tab actions ──────────────────────────────────────── */

  private onAddTab(): void {
    addTab('New tab');
  }

  private onRenameTab(tabId: string, name: string): void {
    if (this.renamingTabId !== tabId) return;
    renameTab(tabId, name);
    this.renamingTabId = null;
  }

  private async startRenamingTab(tabId: string): Promise<void> {
    this.renamingTabId = tabId;
    await this.updateComplete;
    const input = this.querySelector<HTMLInputElement>('.dash-tab-input');
    input?.focus();
    input?.select();
  }

  private async startRenamingDashboard(): Promise<void> {
    this.renamingDashboard = true;
    await this.updateComplete;
    const input = this.querySelector<HTMLInputElement>('.dash-crumb-input');
    input?.focus();
    input?.select();
  }

  private onRenameDashboard(name: string): void {
    if (!this.renamingDashboard) return;
    this.renamingDashboard = false;
    const d = activeDashboard();
    if (!d || !name.trim() || name.trim() === d.name) return;
    void renameDashboard(d.id, name).then((result) => {
      if (result !== true) showToast('error', 'Could not rename this dashboard.');
    });
  }

  /* ── Tile events (bubbled from <dashboard-tile>) ──────── */

  private tileById(id: string): DashboardTile | null {
    return activeTab()?.tiles.find((t) => t.id === id) ?? null;
  }

  private onTileOpen(e: CustomEvent<{ id: string }>): void {
    const tile = this.tileById(e.detail.id);
    if (!tile) return;
    const query = tileExploreQuery(effectiveTile(tile), this.sharedContext());
    activeSection.set('explore');
    document.body.dataset.section = 'explore';
    void restoreFromUrl(query);
  }

  private onTileEdit(e: CustomEvent<{ id: string }>): void {
    const tile = this.tileById(e.detail.id);
    if (tile) this.openBuilder(tile);
  }

  /** Cut a linked tile loose, copying the chart as it is now when it can. */
  private onTileDetach(e: CustomEvent<{ id: string }>): void {
    const tile = this.tileById(e.detail.id);
    if (!tile?.chartId) return;
    const link = linkState(tile.chartId);
    detachTile(tile.id, link.kind === 'ready' ? link.chart : undefined);
    showToast('success', `"${effectiveTile(tile).title}" is now this dashboard's own copy`);
  }

  private onTileSlicers(e: CustomEvent<{ id: string }>): void {
    const tile = this.tileById(e.detail.id);
    if (tile) setTileIgnoresSlicers(tile.id, !tile.ignoreSlicers);
  }

  private onTileRemove(e: CustomEvent<{ id: string }>): void {
    removeTile(e.detail.id);
  }

  private onTileDuplicate(e: CustomEvent<{ id: string }>): void {
    duplicateTile(e.detail.id);
  }

  private onTileRename(e: CustomEvent<{ id: string }>): void {
    const tile = this.tileById(e.detail.id);
    if (!tile) return;
    const title = prompt('Tile title', tile.title);
    if (title === null) return;
    renameTile(tile.id, title);
  }

  /* ── Render: open dashboard ───────────────────────────── */

  private renderToolbar(): TemplateResult {
    const d = activeDashboard();
    const scope = this.scopeFor(d);
    const folder = folders.value.find((f) => f.id === d?.folderId);
    const editable = canEditDashboard(d);
    const editing = dashboardEditing.value && editable;
    const dirty = dashboardDirty.value;
    return html`
      <div class="dash-toolbar">
        <div class="dash-toolbar-left">
          <nav class="dash-crumbs" aria-label="Breadcrumb">
            <button class="dash-crumb-link" @click=${this.onClose}>Dashboards</button>
            ${folder
              ? html`<span class="dash-crumb-sep">/</span><span>${folder.name}</span>`
              : nothing}
            <span class="dash-crumb-sep">/</span>
            <!-- The tour's anchor is here, not on the name: a click swaps the name for an input. -->
            <span class="dash-crumb-title" data-tour="dash-name">
              ${this.renamingDashboard
                ? html`<input
                    class="dash-crumb-input"
                    aria-label="Dashboard name"
                    .value=${d?.name ?? ''}
                    @blur=${(e: Event) => this.onRenameDashboard((e.target as HTMLInputElement).value)}
                    @keydown=${(e: KeyboardEvent) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      if (e.key === 'Escape') this.renamingDashboard = false;
                    }}
                  />`
                : editable
                  ? html`<button
                      class="dash-crumb-current dash-name-edit"
                      title="Rename dashboard"
                      aria-label=${`Rename dashboard ${d?.name ?? ''}`}
                      @click=${this.startRenamingDashboard}
                    >${d?.name ?? ''}</button>`
                  : html`<strong class="dash-crumb-current">${d?.name ?? ''}</strong>`}
            </span>
          </nav>
          <!-- Dashboard settings stay beside the title; clicking the name edits it. -->
          ${editable
            ? html`<dash-dropdown
                class="dash-settings-dd"
                triggerClass="dash-icon-btn"
                triggerTitle="Dashboard settings"
                .triggerContent=${GEAR_GLYPH}
                .panel=${() => html`<dashboard-settings></dashboard-settings>`}
              ></dash-dropdown>`
            : html`<span
                class="dash-readonly"
                title="Shared with you to read. Only someone with Editor access can change it."
                >View only</span
              >`}
        </div>

        <div class="dash-toolbar-right">
          ${editable ? this.renderEditControls(dirty, editing) : nothing}
          <dash-dropdown
            data-tour="dash-time"
            caption="Time"
            label=${dateRangeLabel(scope)}
            panelClass="is-wide"
            alignEnd
            .panel=${() => html`<date-range .scope=${scope}></date-range>`}
          ></dash-dropdown>
          ${this.renderLivePicker(d ?? null)}
          <dash-dropdown
            data-tour="dash-ships"
            caption="Ships"
            label=${shipsLabel(scope)}
            alignEnd
            .panel=${() => html`<ship-multi-select start-open .scope=${scope}></ship-multi-select>`}
          ></dash-dropdown>
          ${this.renderMore()}
        </div>
      </div>
    `;
  }

  /** Add filter: opens the filter dialog; the slicer it makes lands on the open tab. */
  private renderAddFilter(): TemplateResult {
    return html`<button
      class="dash-btn"
      title="Add a filter that narrows every chart on this tab"
      @click=${() => (this.filterDialog = { tile: null })}
    >
      + Add filter
    </button>`;
  }

  /**
   * Everything in the toolbar that writes, in the order it is reached for: Add
   * chart, then (Discard,) Save, then Edit.
   *
   * Absent rather than disabled for a viewer - a row of greyed buttons is four
   * dead ends and a question, where nothing at all is an answer. The **View
   * only** chip beside the name is where the question gets answered.
   */
  private renderEditControls(dirty: boolean, editing: boolean): TemplateResult {
    return html`
      ${this.renderAddChart()} ${this.renderAddFilter()}
      ${dirty
        ? html`<button
            class="dash-btn"
            title="Go back to the last saved version"
            @click=${this.onDiscard}
          >
            Discard
          </button>`
        : nothing}
      <button
        class="dash-btn icon-text ${dirty ? 'is-dirty' : ''}"
        data-tour="dash-save"
        title=${dirty ? 'Unsaved changes - write them now' : 'This dashboard is saved'}
        ?disabled=${!dirty}
        @click=${this.onSave}
      >
        ${SAVE_GLYPH} ${dirty ? 'Save' : 'Saved'}
      </button>
      <button
        class="dash-btn ${editing ? 'is-active' : ''}"
        data-tour="dash-edit"
        title=${editing ? 'Finish editing the layout' : 'Move, resize and remove tiles'}
        @click=${this.onToggleEditing}
      >
        ${editing ? '✓ Done' : '✎ Edit'}
      </button>
    `;
  }

  /**
   * Add chart: straight into the builder when there is no catalog to choose
   * from - the app without a database is the app as it was - and otherwise a
   * two-item menu, new or from the catalog.
   */
  private renderAddChart(): TemplateResult {
    if (!catalogAvailable.value) {
      return html`<button class="dash-btn is-primary" data-tour="dash-add-chart" @click=${() => this.openBuilder(null)}>
        + Add chart
      </button>`;
    }
    const pick = (e: Event, action: () => void): void => {
      (e.currentTarget as HTMLElement).closest<DashDropdown>('dash-dropdown')?.close();
      action();
    };
    return html`<dash-dropdown
      data-tour="dash-add-chart"
      label="+ Add chart"
      triggerClass="is-primary"
      panelClass="is-menu"
      panelRole="menu"
      .panel=${() => html`
        <button
          class="dash-menu-item"
          role="menuitem"
          data-tour="dash-add-new"
          @click=${(e: Event) => pick(e, () => this.openBuilder(null))}
        >
          <span class="dash-menu-label">New chart</span>
          <span class="dash-menu-hint">Build one with the Explore controls</span>
        </button>
        <button
          class="dash-menu-item"
          role="menuitem"
          data-tour="dash-add-catalog"
          @click=${(e: Event) =>
            pick(e, () => {
              this.picking = true;
            })}
        >
          <span class="dash-menu-label">From catalog…</span>
          <span class="dash-menu-hint">Link charts saved by you or your team</span>
        </button>
      `}
    ></dash-dropdown>`;
  }

  /**
   * The only path from an arranged layout to storage.
   *
   * Nothing writes on a drag any more (`dashboard-state.ts`), so this is a real
   * save rather than a confirmation - and a failure is said out loud, because
   * the alternative is someone closing a tab believing their work is stored.
   */
  private async onSave(): Promise<void> {
    const d = activeDashboard();
    if (!d) return;
    const result = await saveActiveDashboard();
    if (result === true) {
      showToast('success', `Saved "${d.name}"`);
      return;
    }
    if (result instanceof DashboardConflictError) {
      // Two people arranged the same dashboard. Neither edit is thrown away
      // without being offered: reload theirs, or overwrite with this one.
      const overwrite = await confirmAction({
        title: 'Someone else saved this dashboard',
        message:
          'It changed since you opened it. Reload to see their version and lose your changes, '
          + 'or overwrite theirs with yours.',
        confirmLabel: 'Overwrite',
        cancelLabel: 'Reload theirs',
        destructive: true,
      });
      if (overwrite) {
        if (await saveActiveDashboard({ force: true })) showToast('success', `Saved "${d.name}"`);
        else showToast('error', 'Still could not save. Your changes are still here.');
      } else {
        await discardActiveChanges();
        showToast('success', 'Reloaded the saved version');
      }
      return;
    }
    showToast('error', `Could not save "${d.name}". Your changes are still here - try again.`);
  }

  private async onDiscard(): Promise<void> {
    if (!dashboardDirty.value) return;
    const ok = await confirmAction({
      title: 'Discard changes?',
      message: 'This dashboard goes back to the last saved version. Unsaved edits are lost.',
      confirmLabel: 'Discard',
      destructive: true,
    });
    if (!ok) return;
    await discardActiveChanges();
    showToast('success', 'Changes discarded');
  }

  /**
   * Ask before anything that would drop unsaved work.
   *
   * The price of an explicit save is that leaving can lose an arrangement, so
   * every exit that is ours asks first: the breadcrumb, Done, and the browser's
   * own unload (which can only be a generic prompt, but is better than silence).
   */
  private async leaveGuard(): Promise<boolean> {
    if (!dashboardDirty.value) return true;
    return confirmAction({
      title: 'Leave without saving?',
      message: 'This dashboard has unsaved changes. Leaving now loses them.',
      confirmLabel: 'Leave',
      destructive: true,
    });
  }

  private onBeforeUnload = (e: BeforeUnloadEvent): void => {
    if (!dashboardDirty.value) return;
    e.preventDefault();
  };

  private async onClose(): Promise<void> {
    if (!(await this.leaveGuard())) return;
    dashboardDirty.set(false);
    closeDashboard();
    // `updated()` releases the borrowed selection on the next render; doing it
    // here as well would race with it.
  }

  private async onToggleEditing(): Promise<void> {
    if (!dashboardEditing.value) {
      dashboardEditing.set(true);
      return;
    }
    // Leaving edit mode with unsaved work is the likeliest way to lose it: the
    // layout stops looking editable, which reads as "committed".
    if (dashboardDirty.value) {
      await this.onSave();
      if (dashboardDirty.value) return;
    }
    dashboardEditing.set(false);
  }

  private renderTabBar(): TemplateResult | typeof nothing {
    const d = activeDashboard();
    if (!d) return nothing;
    const current = activeTab();
    const editable = canEditDashboard(d);
    const editing = dashboardEditing.value && editable;
    return html`
      <div class="dash-tabbar" role="tablist">
        ${d.tabs.map((tab) => html`
          <span class="dash-tab-wrap ${tab.id === current?.id ? 'active' : ''}">
            ${this.renamingTabId === tab.id
              ? html`<input
                  class="dash-tab-input"
                  aria-label="Tab name"
                  .value=${tab.name}
                  @blur=${(e: Event) => this.onRenameTab(tab.id, (e.target as HTMLInputElement).value)}
                  @keydown=${(e: KeyboardEvent) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') this.renamingTabId = null;
                  }}
                />`
              : html`<span class="dash-tab-main"><button
                  class="dash-tab ${tab.id === current?.id ? 'active' : ''}"
                  role="tab"
                  aria-selected=${tab.id === current?.id ? 'true' : 'false'}
                  @click=${() => selectTab(tab.id)}
                  @dblclick=${() => editable && this.startRenamingTab(tab.id)}
                >${tab.name}<span class="dash-tab-count">${chartTiles(tab).length}</span></button>
                ${editable
                  ? html`<button
                      class="dash-tab-edit"
                      title=${`Rename ${tab.name}`}
                      aria-label=${`Rename ${tab.name}`}
                      @click=${() => this.startRenamingTab(tab.id)}
                    >✎</button>`
                  : nothing}</span>`}
            ${editable && d.tabs.length > 1
              ? html`<button
                  class="dash-tab-x"
                  title=${`Delete ${tab.name}`}
                  aria-label=${`Delete ${tab.name}`}
                  @click=${() => deleteTab(tab.id)}
                >✕</button>`
              : nothing}
          </span>
        `)}
        ${editable
          ? html`<button class="dash-tab-add" title="New tab" @click=${this.onAddTab}>+</button>`
          : nothing}
        ${editing
          ? html`<span class="dash-tab-hint"
              >drag tile headers to move · corner to resize</span
            >`
          : nothing}
      </div>
    `;
  }

  override render(): TemplateResult {
    const browsing = dashboardRoute.value === 'browse';
    const d = activeDashboard();
    const tab = activeTab();
    return html`
      <div
        class="dash-root ${d && !d.settings.showTitles ? 'no-titles' : ''} ${d && !d.settings.tileFrames ? 'no-frames' : ''} ${dashboardEditing.value ? 'is-editing' : ''} ${this.kiosk ? 'is-kiosk' : ''}"
        @tile-open=${this.onTileOpen}
        @tile-edit=${this.onTileEdit}
        @tile-remove=${this.onTileRemove}
        @tile-duplicate=${this.onTileDuplicate}
        @tile-rename=${this.onTileRename}
        @tile-detach=${this.onTileDetach}
        @tile-slicers=${this.onTileSlicers}
        @tile-clear-slicers=${() => {
          const d = activeDashboard();
          const t = activeTab();
          if (d && t) clearAllSelections(d, t);
        }}
        @slicer-edit=${(e: CustomEvent<{ id: string }>) => {
          const tile = this.tileById(e.detail.id);
          if (tile) this.filterDialog = { tile };
        }}
        @filter-dialog-close=${() => {
          this.filterDialog = null;
        }}
        @picker-commit=${this.onPickerCommit}
        @picker-close=${() => {
          this.picking = false;
        }}
        @builder-commit=${this.onBuilderCommit}
        @builder-close=${() => {
          this.builder = null;
        }}
      >
        ${browsing || !d || !tab
          ? html`<dashboard-browse></dashboard-browse>`
          : html`
              ${this.renderToolbar()} ${this.renderTabBar()}
              <dashboard-grid
                data-tour="dash-grid"
                .tiles=${tab.tiles}
                .shared=${this.sharedContext()}
                .syncKey=${d.settings.sharedCrosshair ? `nde-dash:${d.id}` : null}
                ?sharedTooltip=${d.settings.sharedTooltip}
                ?float=${d.settings.floatTiles}
                ?editing=${dashboardEditing.value && canEditDashboard(d)}
              ></dashboard-grid>
            `}
        ${this.builder
          ? html`<chart-builder
              .tile=${this.builder.tile}
              .shared=${this.sharedContext()}
            ></chart-builder>`
          : nothing}
        ${this.picking
          ? html`<catalog-picker .linkedIds=${this.linkedOnTab()}></catalog-picker>`
          : nothing}
        ${this.filterDialog
          ? html`<dashboard-filter-dialog
              .tile=${this.filterDialog.tile}
              .ships=${this.sharedContext().ships}
            ></dashboard-filter-dialog>`
          : nothing}
        <dashboard-share-dialog></dashboard-share-dialog>
        ${this.kioskHint
          ? html`<div class="dash-kiosk-hint" role="status">
              Esc to leave full screen${(d?.tabs.length ?? 0) > 1 ? ' · ← → for tabs' : ''}
            </div>`
          : nothing}
      </div>
    `;
  }
}
